import React, { useEffect, useState } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { api } from '../../utils/apiClient';
import ActionButton from '../common/ActionButton';

interface Configuration {
  environment_url: string;
  connect_timeout: number;
  read_timeout: number;
  credential_configured: boolean;
  telemetry_dataset?: string | null;
  telemetry_source?: string | null;
  metric_prefix?: string | null;
}

export default function DynatraceConnection({ projectId, editable }: { projectId: string; editable: boolean }) {
  const path = `/observability/dynatrace?project_id=${encodeURIComponent(projectId)}`;
  const [config, setConfig] = useState<Configuration | null>(null);
  const [token, setToken] = useState('');
  const [remove, setRemove] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setConfig(null); setToken(''); setRemove(false); setReplacing(false); setMessage(''); setError('');
    api.get<Configuration>(path).then(value => { if (active) setConfig(value); })
      .catch(() => { if (active) setError('Could not load Dynatrace settings.'); });
    return () => { active = false; };
  }, [path]);
  const save = async () => {
    if (!config) return;
    setBusy(true); setMessage(''); setError('');
    const secret = token; setToken('');
    let putSucceeded = false;
    try {
      const payload = { environment_url: config.environment_url, connect_timeout: config.connect_timeout,
        read_timeout: config.read_timeout, remove_token: remove,
        telemetry_dataset: config.telemetry_dataset || undefined,
        telemetry_source: config.telemetry_source || undefined,
        metric_prefix: config.metric_prefix || undefined,
        ...(secret && !remove && (!config.credential_configured || replacing) ? { access_token: secret } : {}) };
      const saved = await api.put<Configuration>(path, payload);
      putSucceeded = true;
      const readback = await api.get<Configuration>(path);
      if (['environment_url', 'connect_timeout', 'read_timeout', 'credential_configured', 'telemetry_dataset', 'telemetry_source', 'metric_prefix'].some(
        key => saved[key as keyof Configuration] !== readback[key as keyof Configuration])) throw new Error();
      setConfig(readback); setRemove(false); setReplacing(false); setMessage('Dynatrace settings saved and read back. Connection has not been tested.');
    } catch (err) {
      if (!putSucceeded && err && typeof err === 'object' && 'status' in err && err.status === 422) {
        setError('Dynatrace settings were rejected before saving. Check the URL, token action, timeouts, dataset, source and metric namespace. Re-enter a replacement token if needed.');
      } else {
        setError('Dynatrace settings could not be verified. Changes may already be saved. Reload settings before retrying.');
      }
    }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setMessage(''); setError('');
    try {
      const result = await api.post<{ connected: boolean; message: string }>(`/observability/dynatrace/test?project_id=${encodeURIComponent(projectId)}`, {});
      if (result.connected) setMessage(result.message); else setError(`Connection check failed: ${result.message}`);
    } catch { setError('Saved connection check could not complete. This does not indicate a settings persistence failure.'); }
    finally { setBusy(false); }
  };
  return <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
    <Typography variant="subtitle1" gutterBottom fontWeight="bold">Dynatrace connection</Typography>
    <Typography variant="body2">Catalog access checks the saved connection. Selection uses this dataset when Dynatrace is the active provider.</Typography>
    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
    {config && <>
      <Typography variant="body2" sx={{ mb: 2 }}>Saved connection: {config.credential_configured ? 'Configured' : 'Unconfigured'}</Typography>
      <TextField select fullWidth margin="normal" label="Telemetry dataset" value={config.telemetry_dataset || ''} disabled={!editable || busy}
        onChange={e => setConfig({ ...config, telemetry_dataset: e.target.value })}>
        <MenuItem value="synthetic">Synthetic simulator</MenuItem><MenuItem value="measured">Measured HTTP</MenuItem>
      </TextField>
      <TextField fullWidth margin="normal" label="Telemetry source" value={config.telemetry_source || ''} disabled={!editable || busy}
        helperText="Use the source configured by your telemetry exporter."
        onChange={e => setConfig({ ...config, telemetry_source: e.target.value })} />
      <TextField fullWidth margin="normal" label="Metric namespace" value={config.metric_prefix || ''} disabled={!editable || busy}
        onChange={e => setConfig({ ...config, metric_prefix: e.target.value })} />
      <TextField fullWidth margin="normal" label="Environment API base URL" value={config.environment_url} disabled={!editable || busy}
        placeholder="https://vsx20302.live.dynatrace.com" onChange={e => setConfig({ ...config, environment_url: e.target.value })} />
      {config.credential_configured && <Box sx={{ my: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2">Token saved</Typography>
        <Typography component="span" aria-label="Saved token indicator">••••••••••••</Typography>
        {!replacing && <ActionButton sx={{ ml: { xs: 0, sm: 1 } }} disabled={!editable || busy || remove}
          onClick={() => { setToken(''); setReplacing(true); }}>Replace token</ActionButton>}
      </Box>}
      {(!config.credential_configured || replacing) && <TextField fullWidth margin="normal" type="password" autoComplete="new-password" label="Access token (write only)" value={token}
        helperText="Leave blank to retain the saved token." disabled={!editable || busy || remove}
        onChange={e => setToken(e.target.value)} />}
      <FormControlLabel label="Remove saved token" control={<Checkbox checked={remove} disabled={!editable || busy}
        onChange={e => { setRemove(e.target.checked); setToken(''); setReplacing(false); }} />} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
      <TextField fullWidth margin="normal" label="Connect timeout (1–10 seconds)" type="number" value={config.connect_timeout} disabled={!editable || busy}
        onChange={e => setConfig({ ...config, connect_timeout: Number(e.target.value) })} />
      <TextField fullWidth margin="normal" label="Read timeout (1–30 seconds)" type="number" value={config.read_timeout} disabled={!editable || busy}
        onChange={e => setConfig({ ...config, read_timeout: Number(e.target.value) })} />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto', pt: 3 }}>
        <ActionButton aria-label="Test saved Dynatrace connection" disabled={!editable || busy} onClick={test}>Test Connection</ActionButton>
        <ActionButton aria-label="Save Dynatrace settings" disabled={!editable || busy} onClick={save}>Save Connection</ActionButton></Box>
    </>}
  </Box>;
}
